import styled from 'styled-components';
import { ThemeInterface } from '../../../interfaces/themeInterface';

const getPanelBackground = (theme: ThemeInterface) =>
  theme.name === 'long' ? 'rgba(255, 204, 213, 0.5)' : 'rgba(10, 10, 78, 0.56)';

const getPanelBorder = (theme: ThemeInterface) =>
  theme.name === 'long' ? 'rgba(0, 0, 255, 0.34)' : 'rgba(255, 204, 213, 0.52)';

const getPanelShadow = (theme: ThemeInterface) =>
  theme.name === 'long' ? '0 8px 24px rgba(0, 0, 255, 0.12)' : '0 8px 24px rgba(0, 0, 0, 0.3)';

const getMutedText = (theme: ThemeInterface) =>
  theme.name === 'long' ? 'rgba(0, 0, 0, 0.62)' : 'rgba(255, 255, 255, 0.74)';

const getDrawerBackground = (theme: ThemeInterface) =>
  theme.name === 'long' ? 'rgba(0, 0, 255, 0.98)' : 'rgba(255, 204, 213, 0.98)';

const getDrawerText = (theme: ThemeInterface) => (theme.name === 'long' ? '#ffffff' : '#000000');

const getAccent = (theme: ThemeInterface) => (theme.name === 'long' ? '#0000ff' : '#ffccd5');

const getLoginInputBackground = (theme: ThemeInterface) =>
  theme.name === 'long' ? 'rgba(0, 0, 0, 0.24)' : 'rgba(255, 255, 255, 0.76)';

const getLoginInputBorder = (theme: ThemeInterface) =>
  theme.name === 'long' ? 'rgba(255, 255, 255, 0.62)' : 'rgba(0, 0, 0, 0.32)';

const getLoginPlaceholder = (theme: ThemeInterface) =>
  theme.name === 'long' ? 'rgba(255, 255, 255, 0.76)' : 'rgba(0, 0, 0, 0.56)';

export const MobileShell = styled.main`
  --mobile-pad: 14px;
  position: relative;
  z-index: 1002;
  min-height: 100vh;
  width: 100%;
  padding: 10px var(--mobile-pad) calc(92px + env(safe-area-inset-bottom));
  background: transparent;
  color: ${({ theme }) => theme.text};
  overflow-x: hidden;
`;

export const MobileBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

export const MobileBackdropLayer = styled.div`
  position: absolute;
  inset: 0;
`;

export const MobileBackdropVeil = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) =>
    theme.name === 'long'
      ? 'linear-gradient(180deg, rgba(255, 204, 213, 0.08) 0%, rgba(255, 204, 213, 0.16) 100%)'
      : 'linear-gradient(180deg, rgba(0, 0, 255, 0.08) 0%, rgba(0, 0, 255, 0.16) 100%)'};
`;

export const MobileContent = styled.div`
  position: relative;
  z-index: 1;
`;

export const TopCluster = styled.section`
  position: sticky;
  top: 0;
  z-index: 12;
  display: grid;
  gap: 8px;
  padding: 4px 0 8px;
  background: ${({ theme }) =>
    theme.name === 'long'
      ? 'transparent'
      : 'linear-gradient(180deg, rgba(0, 0, 255, 0.5) 0%, rgba(0, 0, 255, 0.28) 70%, rgba(0, 0, 255, 0) 100%)'};
`;

export const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 2px 2px 0;
`;

export const BrandChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => getPanelBorder(theme)};
  background: ${({ theme }) => getPanelBackground(theme)};
  padding: 4px 9px 4px 5px;
  box-shadow: ${({ theme }) => getPanelShadow(theme)};
  backdrop-filter: blur(8px) saturate(110%);
`;

export const BrandIcon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  display: block;
  flex-shrink: 0;
`;

export const BrandText = styled.span`
  font-family: monospace;
  font-size: 0.63rem;
  letter-spacing: 0.08em;
  text-transform: lowercase;
  line-height: 1;
`;

export const ConversionCard = styled.div`
  background: ${({ theme }) => getPanelBackground(theme)};
  border: 1px solid ${({ theme }) => getPanelBorder(theme)};
  border-radius: 14px;
  padding: 12px;
  box-shadow: ${({ theme }) => getPanelShadow(theme)};
  backdrop-filter: blur(9px) saturate(115%);
`;

export const ConversionTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
`;

export const ConversionTitle = styled.h1`
  margin: 0;
  font-size: 0.93rem;
  letter-spacing: 0.02em;
  line-height: 1.15;
  font-family: monospace;
`;

export const TierPill = styled.span`
  font-family: monospace;
  font-size: 0.62rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid currentColor;
  border-radius: 999px;
  background: ${({ theme }) => getPanelBackground(theme)};
  opacity: 0.9;
  white-space: nowrap;
`;

export const ConversionTagline = styled.p`
  margin: 0 0 8px;
  font-size: 0.7rem;
  line-height: 1.35;
  font-family: monospace;
  opacity: 0.9;
`;

export const ConversionCopy = styled.p`
  margin: 0 0 7px;
  font-size: 0.7rem;
  line-height: 1.45;
  color: ${({ theme }) => getMutedText(theme)};
`;

export const ConversionMeta = styled.p`
  margin: 0 0 10px;
  font-size: 0.61rem;
  font-family: monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.72;
`;

export const ConversionActions = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
`;

export const PrimaryAction = styled.button<{ $fullWidth?: boolean }>`
  grid-column: ${({ $fullWidth }) => ($fullWidth ? '1 / -1' : 'auto')};
  border: none;
  border-radius: 10px;
  padding: 0.68rem 0.72rem;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.background};
  font-family: monospace;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: lowercase;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.42;
    cursor: default;
  }
`;

export const SecondaryAction = styled.button`
  border: 1px solid currentColor;
  border-radius: 10px;
  padding: 0.68rem 0.72rem;
  background: ${({ theme }) => getPanelBackground(theme)};
  color: inherit;
  font-family: monospace;
  font-size: 0.68rem;
  cursor: pointer;
  opacity: 0.88;
  backdrop-filter: blur(8px) saturate(110%);
`;

export const TertiaryAction = styled.button`
  grid-column: 1 / -1;
  border: none;
  background: transparent;
  color: ${({ theme }) => getMutedText(theme)};
  font-family: monospace;
  font-size: 0.62rem;
  letter-spacing: 0.07em;
  text-transform: lowercase;
  text-align: left;
  padding: 0.1rem 0.1rem 0;
  cursor: pointer;
  opacity: 0.9;

  &:hover {
    opacity: 1;
  }
`;

export const DisclaimerLine = styled.p`
  margin: 10px 0 0;
  border-top: 1px solid ${({ theme }) => getPanelBorder(theme)};
  padding-top: 8px;
  font-size: 0.62rem;
  font-family: monospace;
  line-height: 1.35;
  color: ${({ theme }) => getMutedText(theme)};
`;

export const StatsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 6px;
`;

export const StatCard = styled.div`
  border: 1px solid ${({ theme }) => getPanelBorder(theme)};
  background: ${({ theme }) => getPanelBackground(theme)};
  border-radius: 10px;
  padding: 8px;
  box-shadow: ${({ theme }) => getPanelShadow(theme)};
  backdrop-filter: blur(8px) saturate(110%);
`;

export const StatLabel = styled.div`
  font-family: monospace;
  font-size: 0.58rem;
  letter-spacing: 0.08em;
  opacity: 0.62;
  text-transform: uppercase;
`;

export const StatValue = styled.div`
  margin-top: 3px;
  font-family: monospace;
  font-size: 0.8rem;
  font-weight: 600;
`;

export const ExplainerStack = styled.section`
  display: grid;
  gap: 10px;
  margin-top: 12px;
`;

export const ProRawFeedWrap = styled.section`
  margin-top: 12px;

  > section {
    min-height: min(42vh, 360px);
    max-height: min(42vh, 360px);
  }
`;

export const ExplainerCard = styled.article`
  border: 1px solid ${({ theme }) => getPanelBorder(theme)};
  background: ${({ theme }) => getPanelBackground(theme)};
  border-radius: 12px;
  padding: 11px;
  box-shadow: ${({ theme }) => getPanelShadow(theme)};
  backdrop-filter: blur(8px) saturate(110%);
`;

export const ExplainerTitle = styled.h2`
  margin: 0 0 7px;
  font-size: 0.72rem;
  font-family: monospace;
  letter-spacing: 0.07em;
  text-transform: uppercase;
`;

export const ExplainerList = styled.ul`
  margin: 0;
  padding-left: 1rem;
  display: grid;
  gap: 6px;
  font-size: 0.71rem;
  line-height: 1.4;
  color: ${({ theme }) => getMutedText(theme)};
`;

export const ExplainerSteps = styled.ol`
  margin: 0;
  padding-left: 1rem;
  display: grid;
  gap: 6px;
  font-size: 0.71rem;
  line-height: 1.4;
  color: ${({ theme }) => getMutedText(theme)};
`;

export const SnapshotCard = styled.section`
  margin-top: 12px;
  border: 1px solid ${({ theme }) => getPanelBorder(theme)};
  background: ${({ theme }) => getPanelBackground(theme)};
  border-radius: 14px;
  padding: 12px;
  box-shadow: ${({ theme }) => getPanelShadow(theme)};
  backdrop-filter: blur(10px) saturate(115%);
`;

export const SnapshotTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 0.73rem;
  font-family: monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const SnapshotGrid = styled.div`
  display: grid;
  gap: 6px;
`;

export const SnapshotRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid ${({ theme }) => getPanelBorder(theme)};
  border-radius: 8px;
  padding: 7px 8px;
  background: ${({ theme }) =>
    theme.name === 'long' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.06)'};
`;

export const SnapshotLabel = styled.span`
  font-family: monospace;
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.88;
`;

export const SnapshotValue = styled.span`
  font-family: monospace;
  font-size: 0.66rem;
  color: ${({ theme }) => getMutedText(theme)};
`;

export const SnapshotHint = styled.p`
  margin: 9px 0 0;
  font-size: 0.63rem;
  font-family: monospace;
  line-height: 1.35;
  color: ${({ theme }) => getMutedText(theme)};
`;

export const ReachabilityCard = styled.section`
  margin-top: 12px;
  border: 1px solid ${({ theme }) => getPanelBorder(theme)};
  background: ${({ theme }) => getPanelBackground(theme)};
  border-radius: 14px;
  padding: 12px;
  box-shadow: ${({ theme }) => getPanelShadow(theme)};
  backdrop-filter: blur(8px) saturate(110%);
`;

export const ReachabilityTitle = styled.h2`
  margin: 0 0 10px;
  font-size: 0.76rem;
  font-family: monospace;
  letter-spacing: 0.08em;
`;

export const ReachabilityRow = styled.div`
  display: grid;
  grid-template-columns: 118px 1fr auto;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ReachabilityMove = styled.span`
  font-family: monospace;
  font-size: 0.58rem;
  line-height: 1.2;
  opacity: 0.8;
`;

export const ReachabilityBlocks = styled.span`
  font-family: monospace;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  color: ${({ theme }) => getAccent(theme)};
  white-space: nowrap;
  overflow: hidden;
`;

export const ReachabilityProb = styled.span`
  font-family: monospace;
  font-size: 0.7rem;
  opacity: 0.86;
`;

export const DesktopFirstNote = styled.p`
  margin: 16px 4px 110px;
  border: 1px solid ${({ theme }) => getPanelBorder(theme)};
  background: ${({ theme }) => getPanelBackground(theme)};
  border-radius: 10px;
  padding: 8px 10px;
  box-shadow: ${({ theme }) => getPanelShadow(theme)};
  backdrop-filter: blur(8px) saturate(110%);
  font-family: monospace;
  font-size: 0.62rem;
  line-height: 1.4;
  color: ${({ theme }) => getMutedText(theme)};
`;

export const DrawerFrame = styled.section<{
  $translateY: number;
  $dragging: boolean;
}>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1014;
  height: min(62vh, 560px);
  transform: translateY(${({ $translateY }) => `${$translateY}px`});
  transition: ${({ $dragging }) =>
    $dragging ? 'none' : 'transform 280ms cubic-bezier(0.18, 0.8, 0.2, 1)'};
`;

export const DrawerSurface = styled.div`
  height: 100%;
  border-radius: 20px 20px 0 0;
  border-top: 1px solid ${({ theme }) => theme.primary};
  background: ${({ theme }) => getDrawerBackground(theme)};
  color: ${({ theme }) => getDrawerText(theme)};
  box-shadow: 0 -18px 50px rgba(0, 0, 0, 0.35);
  overflow: hidden;
`;

export const DrawerHandle = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  padding: 10px 14px 8px;
  display: grid;
  gap: 6px;
  cursor: ns-resize;
  touch-action: none;
  user-select: none;
  color: inherit;
`;

export const Grabber = styled.span`
  justify-self: center;
  width: 52px;
  height: 4px;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.5;
`;

export const DrawerPreview = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

export const DrawerLabel = styled.span`
  font-family: monospace;
  font-size: 0.76rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.85;
`;

export const DrawerMeta = styled.span`
  font-family: monospace;
  font-size: 0.6rem;
  opacity: 0.7;
`;

export const DrawerBody = styled.div`
  height: calc(100% - 58px);
  overflow-y: auto;
  padding: 0 14px calc(20px + env(safe-area-inset-bottom));
`;

export const DrawerHeadline = styled.h3`
  margin: 8px 0 8px;
  font-size: 0.82rem;
  font-family: monospace;
  letter-spacing: 0.05em;
`;

export const DrawerSummary = styled.p`
  margin: 0 0 10px;
  font-size: 0.75rem;
  line-height: 1.45;
  opacity: 0.82;
`;

export const DrawerMarkdown = styled.div`
  font-size: 0.78rem;
  line-height: 1.5;

  p,
  ul,
  ol,
  blockquote {
    margin: 0 0 0.8rem;
  }

  ul,
  ol {
    padding-left: 1.1rem;
  }
`;

export const LoginOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1016;
  background: rgba(0, 0, 0, 0.28);
  display: flex;
  align-items: flex-end;
`;

export const LoginSheet = styled.div`
  width: 100%;
  border-radius: 18px 18px 0 0;
  background: ${({ theme }) => getDrawerBackground(theme)};
  color: ${({ theme }) => getDrawerText(theme)};
  padding: 14px 14px calc(18px + env(safe-area-inset-bottom));
  border-top: 1px solid ${({ theme }) => theme.primary};
`;

export const LoginTitle = styled.h4`
  margin: 0 0 8px;
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  font-family: monospace;
`;

export const LoginCopy = styled.p`
  margin: 0 0 10px;
  font-size: 0.72rem;
  opacity: 0.82;
`;

export const LoginInput = styled.input`
  width: 100%;
  border: 1px solid ${({ theme }) => getLoginInputBorder(theme)};
  background: ${({ theme }) => getLoginInputBackground(theme)};
  color: ${({ theme }) => getDrawerText(theme)};
  padding: 0.64rem 0.7rem;
  border-radius: 8px;
  font-family: monospace;
  font-size: 0.74rem;
  margin-bottom: 8px;

  &::placeholder {
    color: ${({ theme }) => getLoginPlaceholder(theme)};
    opacity: 1;
  }
`;

export const LoginActions = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
`;

export const LoginMessage = styled.p<{ $type?: 'success' | 'error' }>`
  margin: 8px 0 0;
  font-size: 0.67rem;
  color: ${({ $type }) =>
    $type === 'success' ? '#22c55e' : $type === 'error' ? '#ef4444' : 'inherit'};
`;
